import { IAccount, ICampaign } from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { Provider } from 'mobx-react';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IImpersonationContext } from '../../../extViewmodels';
import { ILocationState } from '../../../models';
import { useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { ADMIN_ACCOUNT_QUERY_KEY } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { AccountOperationsViewModel, AdminPermission } from '../../../viewmodels/AdminViewModels';
import {
	AdminDrawerToolbar,
	AppDrawerToolbarButton,
	SideDrawer,
	StickyAppDrawerContent,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../web/components/Tabs';
import { ExternalIntegrations } from '../../../web/components/integrations/ExternalIntegrations';
import { AdminSocialMediaReportingTable } from '../../../web/components/socialMedia/SocialMediaReportingTable/AdminSocialReportingTable';
import { ScheduleMeetingIcon } from '../../../web/components/svgs/icons/ScheduleMeetingIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { SquareLinkIcon } from '../../../web/components/svgs/icons/SquareLinkIcon';
import { TagsContainer } from '../../../web/containers/PeopleV2/TagsContainer';
import {
	AdminAppAssistantChat,
	SuccessMeetingPrepAppAssistantChat,
} from '../../../web/containers/templates/AssistantChat';
import { ConditionalAccess } from '../../components/ConditionalAccess';
import { DisabledTabContent } from '../../components/accountDetails/AccountDetailsTabView/presentation';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { AccountDetails } from '../../containers/account/AccountDetails';
import {
	AccountDetailsAllowRegistrationEdit,
	AccountDetailsBrandRegistrationStatusIndicator,
	AccountDetailsCampaignRegistrationStatusList,
} from '../../containers/account/AccountDetails/presentation';
import { AccountAutomationsTab } from '../../containers/account/accountTabs/AccountAutomationsTab';
import { AdminIntegrations } from '../../containers/account/accountTabs/AdminIntegrations';
import { GroupEmails } from '../../containers/account/accountTabs/GroupEmails';
import { Imports } from '../../containers/account/accountTabs/Imports';
import { Settings } from '../../containers/account/accountTabs/Settings';
import { TextingManagement } from '../../containers/account/accountTabs/TextingManagement';
import { Users } from '../../containers/account/accountTabs/Users';
import { AccountDetailsTabContentOverview } from './AccountDetailsTabContentOverview';
import { styleSheet } from './styles';
import { useAccountDetailsV2Data } from './useAccountDetailsV2Data';

const ACCOUNT_TABS = [
	{ label: 'Overview', value: 'overview' },
	{ label: 'Website', value: 'website' },
	{ label: 'Custom Content', value: 'content-preferences' },
	{ label: 'Ads', value: 'ads' },
	{ label: 'Users', value: 'users' },
	{ label: 'Imports', value: 'imports' },
	{ label: 'Texting', value: 'texting' },
	{ label: 'Tags', value: 'tags' },
	{ label: 'Email', value: 'group-emails' },
	{ label: 'Social', value: 'social' },
	{ label: 'Automations', value: 'automations' },
	{ label: 'Integrations', value: 'integrations' },
	{ label: 'Settings', value: 'settings' },
] as const;

/** Tabs that remain as placeholders (no V1 equivalent in the tab view) */
const PLACEHOLDER_TABS = new Set<string>([]);

type TabValue = (typeof ACCOUNT_TABS)[number]['value'];

/** Tabs that are conditionally hidden based on account features */
const FEATURE_GATED_TABS = new Set<TabValue>(['website', 'content-preferences', 'ads', 'texting']);

/** Tabs that have editable content (show the Edit button in the header) */
const EDITABLE_TABS = new Set<TabValue>(['overview', 'website', 'content-preferences', 'ads', 'settings']);

export const AdminAccountDetailsV2Page: React.FC = () => {
	const params = useParams<{ accountId: string; tab?: string }>();
	const history = useHistory();
	const fullscreenModal = useFullscreenModal();
	const queryClient = useQueryClient();
	const { account, accountOperations, impersonationContextVM, userSession, isLoading } = useAccountDetailsV2Data(
		params.accountId
	);
	const impersonationContext: IImpersonationContext = { account: { id: params.accountId } };

	const [isEditMode, setIsEditMode] = React.useState(false);
	const [editedCompanyName, setEditedCompanyName] = React.useState<string | undefined>(undefined);
	const handleEditModeChange = React.useCallback((editing: boolean) => {
		setIsEditMode(editing);
		if (!editing) {
			setEditedCompanyName(undefined);
		}
	}, []);
	const [consumedUsers, setConsumedUsers] = React.useState<number | null>(null);

	React.useEffect(() => {
		if (accountOperations) {
			const userCountPromise = accountOperations.getUserCount();
			if (userCountPromise) {
				userCountPromise
					.then(count => setConsumedUsers(count))
					.catch(() => {
						// Silently fail - will show just licensed count
					});
			}
		}
	}, [accountOperations]);

	const getUsersTabLabel = () => {
		const totalLicenses = account?.planDetails?.users;
		if (totalLicenses == null) {
			return 'Users';
		}
		if (consumedUsers != null) {
			return `Users\u00A0(${consumedUsers}/${totalLicenses})`;
		}
		return `Users\u00A0(${totalLicenses})`;
	};

	const validTabValues = new Set<string>(ACCOUNT_TABS.map(t => t.value));
	const selectedTab: TabValue = (validTabValues.has(params.tab) ? params.tab : 'overview') as TabValue;

	const hiddenTabs = React.useMemo(() => {
		const hidden = new Set<TabValue>();
		if (!account) {
			return hidden;
		}
		if (!account.features?.websites?.enabled && !account.features?.websites?.cancelledDate) {
			hidden.add('website');
		}
		if (!account.features?.customContentCalendar?.enabled) {
			hidden.add('content-preferences');
		}
		if (!account.features?.ads?.enabled) {
			hidden.add('ads');
		}
		if (!account.features?.texting?.enabled) {
			hidden.add('texting');
		}
		return hidden;
	}, [account]);

	const visibleTabs = React.useMemo(
		() => ACCOUNT_TABS.filter(tab => !FEATURE_GATED_TABS.has(tab.value) || !hiddenTabs.has(tab.value)),
		[hiddenTabs]
	);

	const onTabChange = (value: string) => {
		const basePath = `/accounts/${params.accountId}`;
		if (value === 'overview') {
			history.push(basePath);
		} else {
			history.push(`${basePath}/${value}`);
		}
	};

	const onScheduleEmailsForClientClicked = React.useCallback(() => {
		const locationState: ILocationState<AccountOperationsViewModel, unknown> = {
			viewModel: accountOperations,
		};
		fullscreenModal.history.push({
			pathname: '/email/bulk-email-approval',
			state: locationState,
		});
	}, [accountOperations, fullscreenModal]);

	const onContentCalendarClicked = React.useCallback(() => {
		const { origin, pathname } = window.location;
		window.open(`${origin}${pathname}#/accounts/${params.accountId}/content-calendar`, '_blank');
	}, [params.accountId]);

	const onAccountViewBulkEmailAttempts = React.useCallback(() => {
		history.push(`/email/account-bulk-historical-report/${params.accountId}`);
	}, [history, params.accountId]);

	const onCampaignViewBulkEmailAttempts = React.useCallback(
		(campaign: ICampaign) => {
			fullscreenModal.history.push(`/email/campaign-bulk-historical-report/${params.accountId}/${campaign.id}`);
		},
		[fullscreenModal, params.accountId]
	);

	const [lastSavedAccount, setLastSavedAccount] = React.useState<IAccount | undefined>(undefined);
	React.useEffect(() => {
		setLastSavedAccount(undefined);
	}, [params.accountId]);

	const onAccountUpdated = React.useCallback(
		(updatedAccount: IAccount) => {
			queryClient.setQueryData([ADMIN_ACCOUNT_QUERY_KEY, params.accountId], updatedAccount);
			setLastSavedAccount(updatedAccount);
		},
		[queryClient, params.accountId]
	);

	const grantTemporaryAccess = React.useCallback(async () => {
		const updatedAccount = await accountOperations.grantTemporaryAccess();
		onAccountUpdated(updatedAccount);
	}, [accountOperations, onAccountUpdated]);

	const onMakeMainContact = React.useCallback(
		async (contactId: string) => {
			if (!account) return;
			const updatedAccount = await accountOperations.saveAccount({
				...account,
				additionalInfo: {
					...account.additionalInfo,
					mainContactUserId: contactId || '',
				},
			});
			if (updatedAccount) {
				onAccountUpdated(updatedAccount);
			}
		},
		[account, accountOperations, onAccountUpdated]
	);

	const renderNotAuthorized = () => (
		<div className={css(styleSheet.unauthorized)}>
			<div>Sorry, you&apos;re not authorized</div>
			<div style={{ fontSize: 50 }}>😿</div>
		</div>
	);

	return (
		<Provider impersonationContext={impersonationContextVM}>
			<AdminDashboardPageLayout
				contentStyles={[styleSheet.content]}
				sidebarChildren={
					<SideDrawer>
						<StickyAppDrawerContent value='ai-assistant'>
							<AdminAppAssistantChat impersonationContext={impersonationContext} />
						</StickyAppDrawerContent>
						<StickyAppDrawerContent value='success-meeting-prep'>
							<SuccessMeetingPrepAppAssistantChat impersonationContext={impersonationContext} />
						</StickyAppDrawerContent>
						<AdminDrawerToolbar>
							<AppDrawerToolbarButton value='ai-assistant' tooltip='AI Assistant'>
								<SparklesIcon />
							</AppDrawerToolbarButton>
							<ConditionalAccess permission={AdminPermission.ModifyAccount}>
								<AppDrawerToolbarButton value='success-meeting-prep' tooltip='Prepare for Meeting'>
									<ScheduleMeetingIcon fill='currentColor' />
								</AppDrawerToolbarButton>
							</ConditionalAccess>
						</AdminDrawerToolbar>
					</SideDrawer>
				}
			>
				<div className={css(styleSheet.container)}>
					<AccountDetails
						hideTabView
						hideOverviewSection
						hideWebsiteSection
						hideAdsSection
						hideCustomContentSection
						hideEditButton={!EDITABLE_TABS.has(selectedTab)}
						externalEditMode={isEditMode}
						onEditModeChange={handleEditModeChange}
						onAccountUpdated={onAccountUpdated}
						onCompanyNameChange={setEditedCompanyName}
						lastSavedAccount={lastSavedAccount}
						extraActionEntities={
							<a href={`#/accounts-v1/${params.accountId}`} className={css(styleSheet.classicViewAction)}>
								Classic View
							</a>
						}
					/>
					{isLoading && !account && (
						<div className={css(styleSheet.loading)}>
							<LoadingSpinner type='large' />
						</div>
					)}
					<TabRoot value={selectedTab} onValueChange={onTabChange}>
						<div className={css(styleSheet.tabListWrapper)}>
							<TabList>
								{visibleTabs.map(tab => (
									<TabTrigger key={tab.value} value={tab.value}>
										{tab.value === 'users' ? getUsersTabLabel() : tab.label}
									</TabTrigger>
								))}
								<button
									className={css(styleSheet.contentCalendarButton)}
									onClick={onContentCalendarClicked}
									type='button'
								>
									Calendar
									<SquareLinkIcon />
								</button>
							</TabList>
						</div>

						<TabContent value='overview'>
							<AccountDetailsTabContentOverview
								hideWebsiteSection
								hideAdsSection
								hideCustomContentSection
								externalEditMode={isEditMode}
								onEditModeChange={handleEditModeChange}
								companyNameOverride={editedCompanyName}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							/>
						</TabContent>

						<TabContent value='website'>
							<AccountDetails
								hideTabView
								hideOverviewSection
								hideHeader
								showOnlyWebsiteModule
								externalEditMode={isEditMode}
								onEditModeChange={handleEditModeChange}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							/>
						</TabContent>

						<TabContent value='content-preferences'>
							<AccountDetails
								hideTabView
								hideOverviewSection
								hideHeader
								showOnlyCustomContentModule
								externalEditMode={isEditMode}
								onEditModeChange={handleEditModeChange}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							/>
						</TabContent>

						<TabContent value='ads'>
							<AccountDetails
								hideTabView
								hideOverviewSection
								hideHeader
								showOnlyAdsModule
								externalEditMode={isEditMode}
								onEditModeChange={handleEditModeChange}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							/>
						</TabContent>

						<TabContent value='users'>
							{account ? (
								<Users
									account={account}
									grantTemporaryAccess={grantTemporaryAccess}
									onMakeMainContact={onMakeMainContact}
								/>
							) : null}
						</TabContent>

						<TabContent value='imports'>
							<Imports accountVM={accountOperations} />
						</TabContent>

						<TabContent value='texting'>
							{account?.features?.texting?.enabled ? (
								<TextingManagement
									account={account}
									statusContent={
										<>
											<AccountDetailsBrandRegistrationStatusIndicator impersonationContext={{ account }} />
											<AccountDetailsCampaignRegistrationStatusList impersonationContext={{ account }} />
											<AccountDetailsAllowRegistrationEdit impersonationContext={{ account }} />
										</>
									}
								/>
							) : (
								<DisabledTabContent>Texting is not enabled for this account.</DisabledTabContent>
							)}
						</TabContent>

						<TabContent value='tags'>
							{account ? <TagsContainer isAdminContext impersonationContext={{ account }} /> : null}
						</TabContent>

						<TabContent value='group-emails'>
							<ConditionalAccess
								elseChildren={renderNotAuthorized()}
								or={AdminPermission.ReadCampaigns}
								permission={AdminPermission.CreateCampaigns}
							>
								{account ? (
									<GroupEmails
										accountId={account.id}
										onScheduleEmailsForClientClicked={onScheduleEmailsForClientClicked}
										onContentCalendarClicked={onContentCalendarClicked}
										onAccountViewBulkEmailAttempts={onAccountViewBulkEmailAttempts}
										onCampaignViewBulkEmailAttempts={onCampaignViewBulkEmailAttempts}
										scrollToBottomPortalId=''
									/>
								) : null}
							</ConditionalAccess>
						</TabContent>

						<TabContent value='automations'>
							<ConditionalAccess
								elseChildren={renderNotAuthorized()}
								permission={AdminPermission.CreateAutomationTemplateOnBehalf}
							>
								{account?.features?.automation?.enabled ? (
									<AccountAutomationsTab account={account} />
								) : (
									<DisabledTabContent>Automations are disabled for this account.</DisabledTabContent>
								)}
							</ConditionalAccess>
						</TabContent>

						<TabContent value='social'>
							<ConditionalAccess
								elseChildren={renderNotAuthorized()}
								or={AdminPermission.ReadCampaigns}
								permission={AdminPermission.CreateCampaigns}
							>
								{account ? (
									<AdminSocialMediaReportingTable impersonationContext={{ account }} scrollToBottomPortalId='' />
								) : null}
							</ConditionalAccess>
						</TabContent>

						<TabContent value='integrations'>
							{account ? (
								<>
									<h3>Integrations (Current)</h3>
									<ExternalIntegrations impersonationContext={{ account }} />
									<h3>Integrations (Legacy)</h3>
									<AdminIntegrations accountVM={accountOperations} adminUserSession={userSession} />
								</>
							) : null}
						</TabContent>

						<TabContent value='settings'>
							<AccountDetails
								hideTabView
								hideOverviewSection
								hideHeader
								hideWebsiteSection
								hideAdsSection
								hideCustomContentSection
								externalEditMode={isEditMode}
								onEditModeChange={handleEditModeChange}
								onAccountUpdated={onAccountUpdated}
								lastSavedAccount={lastSavedAccount}
							>
								<Settings scrollToBottomPortalId='' />
							</AccountDetails>
						</TabContent>

						{ACCOUNT_TABS.filter(tab => PLACEHOLDER_TABS.has(tab.value)).map(tab => (
							<TabContent key={tab.value} value={tab.value}>
								<PlaceholderTabContent tabName={tab.label} />
							</TabContent>
						))}
					</TabRoot>
				</div>
			</AdminDashboardPageLayout>
		</Provider>
	);
};

const PlaceholderTabContent = ({ tabName }: { tabName: string }) => {
	return <div className={css(styleSheet.placeholderContent)}>Placeholder for {tabName} tab</div>;
};
